import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Heart, Star, Trophy, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

export function SuccessStories() {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [story, setStory] = useState("");
  const [open, setOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: stories } = useQuery({
    queryKey: ['success-stories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('success_stories')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('likes_count', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Get user profiles
      const userIds = [...new Set(data.map(s => s.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Get user's likes
      const { data: userLikes } = await supabase
        .from('success_story_likes')
        .select('story_id')
        .eq('user_id', user?.id || '');

      const likedIds = new Set(userLikes?.map(l => l.story_id) || []);

      return data.map(s => ({
        ...s,
        user_profile: profileMap.get(s.user_id),
        user_liked: likedIds.has(s.id),
      }));
    },
  });

  const createStoryMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !title.trim() || !story.trim()) return;

      const { error } = await supabase
        .from('success_stories')
        .insert({
          user_id: user.id,
          title: title.trim(),
          story: story.trim(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['success-stories'] });
      toast.success("Success story shared!");
      setTitle("");
      setStory("");
      setOpen(false);
    },
  });

  const toggleLikeMutation = useMutation({
    mutationFn: async ({ storyId, shouldLike }: { storyId: string; shouldLike: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");

      if (shouldLike) {
        const { error } = await supabase
          .from('success_story_likes')
          .insert({
            story_id: storyId,
            user_id: user.id,
          });
        
        if (error) throw error;

        // Increment likes count
        const { data: currentStory } = await supabase
          .from('success_stories')
          .select('likes_count')
          .eq('id', storyId)
          .single();

        await supabase
          .from('success_stories')
          .update({ likes_count: (currentStory?.likes_count || 0) + 1 })
          .eq('id', storyId);
      } else {
        const { error } = await supabase
          .from('success_story_likes')
          .delete()
          .eq('story_id', storyId)
          .eq('user_id', user.id);
        
        if (error) throw error;

        // Decrement likes count
        const { data: currentStory } = await supabase
          .from('success_stories')
          .select('likes_count')
          .eq('id', storyId)
          .single();

        await supabase
          .from('success_stories')
          .update({ likes_count: Math.max(0, (currentStory?.likes_count || 0) - 1) })
          .eq('id', storyId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['success-stories'] });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-primary" />
                Success Stories
              </CardTitle>
              <CardDescription>Get inspired by others' achievements</CardDescription>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Share Your Story
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Share Your Success Story</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Title</label>
                    <Input
                      placeholder="My transformation journey..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Your Story</label>
                    <Textarea
                      placeholder="Share what worked, challenges you overcame, and advice for others..."
                      value={story}
                      onChange={(e) => setStory(e.target.value)}
                      rows={6}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => createStoryMutation.mutate()}
                    disabled={!title.trim() || !story.trim() || createStoryMutation.isPending}
                  >
                    Share Story
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {stories && stories.length > 0 ? (
            <div className="space-y-4">
              {stories.map((story: any) => (
                <Card key={story.id} className={story.is_featured ? 'border-primary/40 bg-primary/5' : ''}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {story.user_profile?.display_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{story.user_profile?.display_name || 'User'}</p>
                          {story.is_featured && (
                            <Badge variant="default" className="text-xs gap-1">
                              <Trophy className="h-3 w-3" />
                              Featured
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-3">{story.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{story.story}</p>
                    <div className="flex items-center gap-4 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`gap-1 ${story.user_liked ? 'text-secondary' : 'text-muted-foreground'}`}
                        onClick={() => toggleLikeMutation.mutate({
                          storyId: story.id,
                          shouldLike: !story.user_liked,
                        })}
                        disabled={toggleLikeMutation.isPending}
                      >
                        <Heart className={`h-4 w-4 ${story.user_liked ? 'fill-current' : ''}`} />
                        <span>{story.likes_count || 0}</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Star className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No success stories yet. Be the first to share your journey!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
